import {AttrsSchema, Component} from 'panel';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import uniqWith from 'lodash/uniqWith';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';

import {getMpContext} from 'common/mp-context';
import {baseComparator, replaceByIndex} from 'common/util/array';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import type {AutoResizingInput} from 'common/components/autoresizing-input';
import {SelectedChannelsChangedEvent} from 'common/widgets/slack-channel-selector/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {ToggleOption} from 'common/components/toggle/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {executeReportQuery} from 'common/report/report-manager/query';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {InsightsSeriesTree} from 'common/report/insights/util/types';
import {CustomAlertsCountResults} from 'app_api/projects/alerts/custom/types';

import {template} from './template';
import css from './index.inline.styl';
import {
  EmailSubscription,
  SlackChannelSubscription,
  SlackWebookSubscription,
  Subscription,
  SubscriptionType,
} from './types';
import {customAlertApi, projectApi} from './api';
import {
  ANOMALY_DETECTION_TRAINING_MODE,
  ANY_VALUE,
  CONFIDENCE_INTERVAL_OPTIONS,
  CUSTOM_ALERT_CONDITION_TEXT,
  CUSTOM_ALERT_CRITERIA_TYPE,
  CUSTOM_ALERT_FREQUENCY,
  CUSTOM_ALERT_OPERATOR,
  CUSTOM_ALERT_TYPE,
  CUSTOM_ALERT_VALIDATION_ERROR,
  CUSTOM_ALERT_VALUE_TYPE,
  DEFAULT_ALERT_NAME,
} from './constants';
import {customAlertTrackingProperties, isAnomalyDetectionVizEnabled, validateSlackWebhook} from './utils';

// type CustomAlertElmRefs = 'thresholdInput';

interface CustomAlertState {
  allProjectMembers: any[];
  projectMembersLoaded: boolean;
  timeUnit: string;
  alertFrequency: number;
  alertName: string;
  alertOp: string;
  alertThreshold: number;
  alertType: string;
  alertConditionText: string;
  alertValueType: string;
  alertCriteriaType: string;
  confidenceInterval: number;
  trainingMode: string;
  errors: {[input: string]: any};
  elmRefs: {
    thresholdInput?: HTMLInputElement | AutoResizingInput;
  };
  isEditing: boolean;
  readOnly: boolean;
  recipients: any[];
  subscriptions: {
    [SubscriptionType.Email]: EmailSubscription[];
    [SubscriptionType.SlackWebhook]: SlackWebookSubscription[];
    [SubscriptionType.SlackChannel]: SlackChannelSubscription[];
  };
  thresholdInputFocused: boolean;
  selectedAlertKeys: Array<{
    displayHeader: any;
    displayValue: any;
    header: any;
    value: any;
  }>;
  userEmail: string;
  slackTeamId: string;
  bookmarkParams: TODO;
  anomalyDetectionResults: TODO;
  anomalyDetectionFilteredResults: TODO;
  loadingChart: boolean;
}

const RESETTABLE_STATE = {
  alertFrequency: CUSTOM_ALERT_FREQUENCY.Day,
  alertName: ``,
  alertOp: CUSTOM_ALERT_OPERATOR.above,
  alertThreshold: null,
  alertType: CUSTOM_ALERT_TYPE.absolute,
  alertConditionText: CUSTOM_ALERT_CONDITION_TEXT.increase,
  alertValueType: CUSTOM_ALERT_TYPE.absolute,
  alertCriteriaType: CUSTOM_ALERT_CRITERIA_TYPE.threshold,
  confidenceInterval: CONFIDENCE_INTERVAL_OPTIONS[`95%`],
  trainingMode: ANOMALY_DETECTION_TRAINING_MODE.agile,
  errors: {},
  elmRefs: {},
  readOnly: false,
  recipients: [],
  subscriptions: {
    [SubscriptionType.Email]: [],
    [SubscriptionType.SlackWebhook]: [],
    [SubscriptionType.SlackChannel]: [],
  },
  selectedAlertKeys: [],
  thresholdInputFocused: false,
  bookmarkParams: null,
  anomalyDetectionResults: null,
  anomalyDetectionFilteredResults: null,
  loadingChart: false,
};

export interface CustomAlertJsxProps {
  attrs?: {
    'alert-condition-keys'?: TODO; // set when editing/viewing existing alert
    'alert-condition-op'?: string;
    'alert-condition-threshold'?: number;
    'alert-condition-type'?: string;
    'alert-confidence-interval'?: number;
    'alert-training-mode'?: string;
    'alert-frequency'?: number;
    'alert-id'?: number;
    'alert-name'?: string;
    'bookmark-id'?: number;
    'enable-condition-types'?: boolean;
    'is-anomaly-detection-control-disabled'?: boolean;
    'is-editing-mode'?: boolean;
    'is-modal-open'?: boolean;
    'is-percentage-threshold'?: boolean;
    'project-id'?: number;
    'workspace-id'?: number;
    'read-only'?: boolean;
    recipients?: Subscription[];
    'report-type'?: BookmarkType;
    'results-keys-and-values'?: TODO | string[]; // set when creating new custom alert
    'time-unit'?: string;
    'date-range-interval-days'?: number;
    'bookmark-params'?: TODO;
  };
}

export class CustomAlert extends Component<
  CustomAlertState,
  CustomAlertJsxProps['attrs'],
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<CustomAlertJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'alert-condition-keys': `json`, // set when editing/viewing existing alert
      'alert-condition-op': `string`,
      'alert-condition-threshold': `number`,
      'alert-condition-type': `string`,
      'alert-confidence-interval': `number`,
      'alert-training-mode': `string`,
      'alert-frequency': `number`,
      'alert-id': `number`,
      'alert-name': `string`,
      'bookmark-id': `number`,
      'enable-condition-types': `boolean`,
      'is-anomaly-detection-control-disabled': `boolean`,
      'is-editing-mode': `boolean`,
      'is-modal-open': `boolean`,
      'is-percentage-threshold': `boolean`,
      'project-id': `number`,
      'workspace-id': `number`,
      'read-only': `boolean`,
      recipients: `json`,
      'report-type': {type: `string`, enum: Object.values(BookmarkType)},
      'results-keys-and-values': `json`, // set when creating new custom alert
      'time-unit': `string`,
      'date-range-interval-days': `number`,
      'bookmark-params': `json`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        ...RESETTABLE_STATE,
        allProjectMembers: [],
        isEditing: false,
        projectMembersLoaded: false,
        timeUnit: ``,
        userEmail: null,
        slackTeamId: null,
      },
      contexts: [`currentProject` as const, `urlManager` as const, `alertLimits` as const],
      helpers: {
        SubscriptionType,

        CUSTOM_ALERT_FREQUENCY,
        CUSTOM_ALERT_OPERATOR,
        CUSTOM_ALERT_TYPE,
        DEFAULT_ALERT_NAME,
        isAnomalyDetectionVizEnabled: () => {
          if (this.isConnected) {
            const currentProject = this.getContext(`currentProject`).get().value;
            return isAnomalyDetectionVizEnabled(currentProject);
          }
          return false;
        },
        canFireAnomalyDetectionQuery: () => {
          return this.helpers.isAnomalyDetectionVizEnabled() && !this.helpers.isAnomalyDetectionToggleDisabled();
        },
        isLongDateRange: () => {
          // When a date range's max history days is greater than 359, we want to limit the alerting frequency.
          return this.attr(`date-range-interval-days`) > 359;
        },
        getModalTitle: () => {
          const {isEditing, readOnly} = this.state;
          if (readOnly) {
            return this.attr(`alert-name`);
          } else {
            return isEditing ? `Edit Custom Alert` : `Create Custom Alert`;
          }
        },
        handleAlertNameChange: (ev) => {
          const error = this.validateName(ev.target.value);
          if (!error) {
            this.update({alertName: ev.target.value});
          }
        },
        getSelectedFrequency: () =>
          Object.keys(CUSTOM_ALERT_FREQUENCY).find((k) => CUSTOM_ALERT_FREQUENCY[k] === this.state.alertFrequency),

        getAlertFrequencyForReadOnly: () => {
          const frequency = this.helpers.getSelectedFrequency();
          if (frequency === `Day`) {
            return `Daily`;
          } else {
            return frequency + `ly`;
          }
        },
        handleSelectFrequency: (ev) => {
          this.update({alertFrequency: parseInt(CUSTOM_ALERT_FREQUENCY[ev.detail.value])});
        },
        getAlertConditionText: (key) => {
          return key.header === `$event` || key.value === ANY_VALUE
            ? this.helpers.getAlertConditionValueText(key)
            : key.displayHeader + ` is ` + this.helpers.getAlertConditionValueText(key);
        },
        getAlertConditionKeyIconName: (key) => {
          //TODO @shayan turn this into const dictionary
          switch (key.header) {
            case `$event`:
              return `event`;
            case `$cohort`:
              return `community`;
            case `$analysis`:
              return `funnels-tab`;
            default:
              return `type-text`;
          }
        },
        getAlertConditionValueText: (key) => {
          const keyValue = key.displayValue || key.value;
          return keyValue === ANY_VALUE ? `Any ${key.displayHeader}` : keyValue || key;
        },
        getAlertKeyOptions: (key, idx) => {
          const alertKeyOptions = [];
          if (this.hasAnySegmentGroup()) {
            alertKeyOptions.push({
              items: [
                {
                  label: `Any ${key.displayName}`,
                  value: ANY_VALUE,
                  isSelected: this.state.selectedAlertKeys[idx].value === ANY_VALUE,
                },
              ],
            });
          }
          alertKeyOptions.push({
            items: [
              ...sortBy(
                key.values.map((value, index) => ({
                  isSelected: this.state.selectedAlertKeys[idx].value === value,
                  label: key.displayValues[index],
                  value,
                })),
                [`label`, `value`],
              ),
            ],
          });
          return alertKeyOptions;
        },
        handleConditionOpChange: (ev) => {
          this.update({alertOp: CUSTOM_ALERT_OPERATOR[ev.detail.value]});
        },
        handleConditionKeyChange: (ev, idx) => {
          const selectedKeyIdx = this.hasExcludedLastStepKey() ? idx + 1 : idx;
          const {errors, selectedAlertKeys} = this.state;
          const selectedKey = selectedAlertKeys[selectedKeyIdx];
          this.update({
            errors: {...errors, alertConditionKeys: null},
            selectedAlertKeys: replaceByIndex(selectedAlertKeys, selectedKeyIdx, {
              header: selectedKey.header,
              value: ev.detail.item.value,
              displayHeader: selectedKey.displayHeader,
            }),
          });
          this.helpers.fetchAnomalyResults(false);
        },
        displayKeyConditionErrorByIndex: (idx) => isNil(this.state.selectedAlertKeys[idx].value),
        // old custom alert functions
        getSelectedConditionOp: () =>
          Object.keys(CUSTOM_ALERT_OPERATOR).find((k) => CUSTOM_ALERT_OPERATOR[k] === this.state.alertOp),
        // begin anomaly detection functions
        getAnomalyDetectionInfoTooltipText: () => {
          return `The confidence interval reflects our level of confidence that the anomalous data point represents
          an unexpected change. Generally, higher confidence levels result in fewer anomalies detected.`;
        },
        getAnomalyDetectionDisabledTooltipText: () => {
          return `Anomaly detection is only supported on line charts without comparisons, rolling ranges, cumulative types, or cohorts overtime.
          To access all alerting options, change the report chart type to a line chart and remove unsupported options.`;
        },
        getAnomalyDetectionBetaLabelText: () => {
          return `Currently, hourly notifications may result in more false positives due to reduced alert accuracy.`;
        },
        getSelectedConfidenceIntervalPercent: () => {
          return Object.keys(CONFIDENCE_INTERVAL_OPTIONS).find(
            (key) => CONFIDENCE_INTERVAL_OPTIONS[key] === this.state.confidenceInterval,
          );
        },
        handleConfidenceIntervalChange: (ev) => {
          const confidenceIntervalPercent = ev.detail.value;
          this.update({confidenceInterval: CONFIDENCE_INTERVAL_OPTIONS[confidenceIntervalPercent]});
          this.helpers.fetchAnomalyResults(true);
        },
        handleAlertCriteriaTypeChange: (ev) => {
          mixpanel.track(
            `[Custom Alerts] Alert criteria type changed`,
            this.trackingProperties({'Alert Criteria Type': ev.detail.selected}),
          );
          this.update({alertCriteriaType: ev.detail.selected});
        },
        isAnomalyDetectionToggleDisabled: () => {
          return this.attr(`is-anomaly-detection-control-disabled`);
        },
        getSelectedSegmentsForAnomalyDetection: (series: InsightsSeriesTree, keyIndex = 0) => {
          const selectedAlertKeys = this.state.selectedAlertKeys;
          const selectedSeries = {};
          if (keyIndex === selectedAlertKeys.length) {
            return series;
          } else {
            const segment = selectedAlertKeys[keyIndex];
            if (segment.value === ANY_VALUE) {
              for (const key in series) {
                if (segment.value !== RESULTS_OVERALL_SUM_KEY) {
                  const returnedSeries = this.helpers.getSelectedSegmentsForAnomalyDetection(
                    series[key] as InsightsSeriesTree,
                    keyIndex + 1,
                  );
                  selectedSeries[key] = returnedSeries;
                }
              }
            } else if (Object.keys(series).includes(segment.value)) {
              const returnedSeries = this.helpers.getSelectedSegmentsForAnomalyDetection(
                series[segment.value] as InsightsSeriesTree,
                keyIndex + 1,
              );
              selectedSeries[segment.value] = returnedSeries;
            }

            return selectedSeries;
          }
        },
        getBookmarkParamsWithoutLegendForAnomalyDetectionPreview: () => {
          // For the anomaly detection preview, we want to show the filtered results based on
          // the selected alert keys making the legend obsolete. We delete it instead and let the
          // chart handle the default legend versus trying to recreate it based on the selected alert keys
          const bookmarkParams = this.state.bookmarkParams;
          delete bookmarkParams.legend;
          return bookmarkParams;
        },
        fetchAnomalyResults: async (requery = true) => {
          if (this.helpers.canFireAnomalyDetectionQuery()) {
            let results = this.state.anomalyDetectionResults;

            if ((isEmpty(results) || requery) && this.state.bookmarkParams) {
              this.update({loadingChart: true});
              const bookmarkParams = this.state.bookmarkParams;

              bookmarkParams[`forecastComparison`] = {
                trainingMode: this.state.trainingMode || `agile`,
                confidence: this.state.confidenceInterval || parseFloat(`0.95`),
              };

              const postPayload = {
                bookmark: bookmarkParams,
                queryLimits: {limit: 50},
                use_query_cache: true,
                use_query_sampling: false,
              };

              const queryParams = {
                ...(this.attr(`workspace-id`) ? {workspace_id: this.attr(`workspace-id`).toString()} : {}),
                project_id: this.attr(`project-id`).toString(),
              };

              const response = await executeReportQuery(`insights`, queryParams, postPayload, null, true);
              results = await response.json();
            }

            const filteredResults = JSON.parse(JSON.stringify(results));
            if (!this.isErrorResult(results)) {
              const selectedForecastSeries = this.helpers.getSelectedSegmentsForAnomalyDetection(
                filteredResults.forecast_comparison.series,
              );
              filteredResults.forecast_comparison.series = selectedForecastSeries;
              const selectedSeries = this.helpers.getSelectedSegmentsForAnomalyDetection(filteredResults.series);
              filteredResults.series = selectedSeries;
            }

            this.update({
              anomalyDetectionResults: results,
              anomalyDetectionFilteredResults: filteredResults,
              loadingChart: false,
            });
          }
        },
        isErrorResult: () => {
          return this.isErrorResult(this.state.anomalyDetectionResults);
        },
        canSaveNewAnomalyAlerts: () => {
          const results: CustomAlertsCountResults = this.getContext(`alertLimits`).get().value ?? null;
          return results?.is_below_limit;
        },
        getAnomalyAlertLimitTooltipText: () => {
          return `Your organization has reached the limit for anomaly detection alerts.
          You can create a custom threshold alert or delete existing anomaly detection alerts.`;
        },
        //begin new custom alert improvements functions
        getCustomAlertConditionTexts: () => {
          return {
            [CUSTOM_ALERT_CONDITION_TEXT.increase]: `is above`,
            [CUSTOM_ALERT_CONDITION_TEXT.decrease]: `is below`,
            ...(this.attr(`enable-condition-types`)
              ? {
                  [CUSTOM_ALERT_CONDITION_TEXT.increaseBy]: `increases by more than`,
                  [CUSTOM_ALERT_CONDITION_TEXT.decreaseBy]: `decreases by more than`,
                }
              : null),
          };
        },
        getCompareToDateSentence: () => {
          return `Compared to the previous ${this.state.timeUnit}`;
        },
        handleCustomAlertConditionTextChange: (ev) => {
          const {value} = ev.detail;
          const alertTypeTextOptions = this.helpers.getCustomAlertConditionTexts();
          const selected = Object.keys(alertTypeTextOptions).find((k) => alertTypeTextOptions[k] === value);
          const alertConditionText = selected;
          const {alertOp, alertType} = this.helpers.getAlertTypeAndOpFromTextAndValue(
            alertConditionText,
            this.state.alertValueType,
          );
          this.update({alertConditionText, alertOp, alertType});
          this.validateThreshold();
        },
        handleCustomAlertValueTypeChange: (ev) => {
          const alertValueType = CUSTOM_ALERT_VALUE_TYPE[ev.detail.selected];
          const {alertOp, alertType} = this.helpers.getAlertTypeAndOpFromTextAndValue(
            this.state.alertConditionText,
            alertValueType,
          );
          this.update({alertValueType, alertOp, alertType});
        },
        getAlertTypeAndOpFromTextAndValue: (alertConditionText: string, alertValueType: string) => {
          let alertType = this.state.alertType;
          const alertOp = [CUSTOM_ALERT_CONDITION_TEXT.increase, CUSTOM_ALERT_CONDITION_TEXT.increaseBy].includes(
            alertConditionText,
          )
            ? CUSTOM_ALERT_OPERATOR.above
            : CUSTOM_ALERT_OPERATOR.below;
          switch (alertConditionText) {
            case CUSTOM_ALERT_CONDITION_TEXT.increase:
            case CUSTOM_ALERT_CONDITION_TEXT.decrease:
              alertType = CUSTOM_ALERT_TYPE.absolute;
              break;
            case CUSTOM_ALERT_CONDITION_TEXT.increaseBy:
            case CUSTOM_ALERT_CONDITION_TEXT.decreaseBy:
              alertType =
                alertValueType === CUSTOM_ALERT_VALUE_TYPE.absolute
                  ? CUSTOM_ALERT_TYPE.relativeValue
                  : CUSTOM_ALERT_TYPE.relativePercent;
              break;
          }
          return {alertOp, alertType};
        },
        getAlertConditionTextAndValueFromTypeAndOp: (alertType: string, alertOp: string) => {
          let alertConditionText = this.state.alertConditionText;
          const alertValueType = [CUSTOM_ALERT_TYPE.absolute, CUSTOM_ALERT_TYPE.relativeValue].includes(alertType)
            ? CUSTOM_ALERT_VALUE_TYPE.absolute
            : CUSTOM_ALERT_VALUE_TYPE.percent;
          if (alertOp === CUSTOM_ALERT_OPERATOR.above) {
            alertConditionText =
              alertType === CUSTOM_ALERT_TYPE.absolute
                ? CUSTOM_ALERT_CONDITION_TEXT.increase
                : CUSTOM_ALERT_CONDITION_TEXT.increaseBy;
          } else if (alertOp === CUSTOM_ALERT_OPERATOR.below) {
            alertConditionText =
              alertType === CUSTOM_ALERT_TYPE.absolute
                ? CUSTOM_ALERT_CONDITION_TEXT.decrease
                : CUSTOM_ALERT_CONDITION_TEXT.decreaseBy;
          }
          return {alertConditionText, alertValueType};
        },
        getAlertFrequencyToggleOptions: (): ToggleOption[] => {
          const excludeHigherFrequencies = this.helpers.isLongDateRange();
          return Object.entries(this.helpers.CUSTOM_ALERT_FREQUENCY).map((frequency) => {
            const isDisabled =
              excludeHigherFrequencies &&
              (frequency[1] === CUSTOM_ALERT_FREQUENCY.Hour || frequency[1] === CUSTOM_ALERT_FREQUENCY.Day);
            return {
              label: frequency[0],
              value: frequency[0],
              ...(isDisabled
                ? {
                    tooltip: {
                      text: `When the report criteria references yearly data, frequency of alerting is limited.`,
                      placement: Placement.Top,
                      size: DisplaySize.Medium,
                    },
                  }
                : {}),
              disabled: isDisabled,
            };
          });
        },
        handleToggleFrequency: (ev) => {
          this.update({alertFrequency: parseInt(CUSTOM_ALERT_FREQUENCY[ev.detail.selected])});
        },
        //end new custom alert improvements functions
        // TODO opslead
        // eslint-disable-next-line sonarjs/cognitive-complexity
        handleThresholdChange: (ev) => {
          const {value} = ev.target;
          const error = this.validateThreshold({thresholdStr: value});
          if (!error) {
            if (value.length > 0) {
              // this isAbsolutePercentageThreshold only applies to funnels for now
              // as we only support overall conversion rate, which is stored as absolute decimal value (vs percentage value)
              // threshold in insights of relative-percent type is store as percentage value
              const isAbsolutePercentageThreshold = this.attr(`is-percentage-threshold`);
              let alertThreshold = isAbsolutePercentageThreshold ? parseFloat(value) / 100 : parseFloat(value);
              let alertThresholdInputString = alertThreshold.toString();

              if (this.state.alertType === CUSTOM_ALERT_TYPE.relativePercent && !Number.isInteger(alertThreshold)) {
                const fixedDecimal = alertThreshold.toFixed(isAbsolutePercentageThreshold ? 4 : 2);
                alertThreshold = parseFloat(fixedDecimal);

                // still need a `toFixed(2)` after `*100` is because in Javascript 0.2222 * 100 => 22.220000000000002
                if (isAbsolutePercentageThreshold) {
                  alertThresholdInputString = (alertThreshold * 100).toFixed(2).toString();
                }
              }
              const thresholdInputElm = this.state.elmRefs.thresholdInput;
              if (thresholdInputElm) {
                (thresholdInputElm as AutoResizingInput).value = alertThresholdInputString;
              }
              this.update({alertThreshold});
            } else {
              this.update({alertThreshold: null});
            }
          }
        },
        handleThresholdFocus: () => {
          this.update({thresholdInputFocused: true});
        },
        handleThresholdBlur: () => {
          this.update({thresholdInputFocused: false});
        },
        getRecipientAttrs: () => {
          const {allProjectMembers, recipients, projectMembersLoaded} = this.state;
          return recipients.map((r) => ({
            email: r.data.recipient,
            invalidAddress: r.invalidAddress,
            name: projectMembersLoaded
              ? (allProjectMembers.find((pm) => pm.email === r.data.recipient) || {name: ``}).name
              : ``,
          }));
        },
        handleRecipientsChanged: (ev) => {
          const {errors} = this.state;
          this.update({
            recipients: ev.detail.recipients.map((r) => ({
              data: {
                recipient: r.email,
              },
              invalidAddress: !!r.invalidAddress,
              name: r.name,
              type: `email`,
            })),
            errors: {
              ...errors,
              recipients: ev.detail.recipients.some((r) => !!r.invalidAddress)
                ? CUSTOM_ALERT_VALIDATION_ERROR.INVALID_EMAIL
                : null,
            },
          });
        },
        getNonEmptyValidRecipientCount: (): number => {
          const {recipients} = this.state;
          return recipients.filter((recipient) => this.isNonEmptyValidRecipient(recipient)).length;
        },
        clickedCancel: () => {
          mixpanel.track(`[Custom Alerts] Dismissed custom alert modal`, this.trackingProperties());
          this.dispatchEvent(new CustomEvent(`cancel`));
          this.resetCustomAlertModal();
        },
        clickedSave: async () => {
          this.validateMultiKeyCondition();
          this.validateName(this.state.alertName);
          this.produceUpdate((state) => {
            for (const subscription of state.subscriptions[SubscriptionType.SlackWebhook]) {
              subscription.error = validateSlackWebhook(subscription);
            }
          });
          const {
            errors,
            alertName,
            alertFrequency,
            alertOp,
            alertThreshold,
            alertType,
            recipients,
            subscriptions,
            isEditing,
            selectedAlertKeys,
            alertCriteriaType,
            confidenceInterval,
            trainingMode,
          } = this.state;
          const hasInvalidSubscriptions = subscriptions[SubscriptionType.SlackWebhook].find(
            (subscription) => subscription.error !== null,
          );
          if (Object.values(errors).some(Boolean) || hasInvalidSubscriptions) {
            // invalid form
            return;
          }
          const projectId = this.attr(`project-id`);
          const validRecipients = recipients.filter((r) => this.isNonEmptyValidRecipient(r));
          const webhookSubscriptions = subscriptions[SubscriptionType.SlackWebhook];
          const channelSubscriptions = subscriptions[SubscriptionType.SlackChannel];
          const savedAlertThreshold = alertThreshold || 0; // the threshold is null initially with placeholder 0

          const isAnomalyDetection =
            alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection &&
            !this.helpers.isAnomalyDetectionToggleDisabled();
          const savedAlertType = isAnomalyDetection ? CUSTOM_ALERT_TYPE.anomaly : alertType;
          const savedAlertOp = isAnomalyDetection ? CUSTOM_ALERT_OPERATOR.outsideRangeExcluding : alertOp;

          const params = {
            name: alertName,
            frequency: alertFrequency,
            condition: {
              type: savedAlertType,
              keys: selectedAlertKeys.map((key) => pick(key, [`header`, `value`])),
              op: savedAlertOp,
              ...(isAnomalyDetection ? {confidence: confidenceInterval, trainingMode} : {value: savedAlertThreshold}),
            },
            subscriptions: [...validRecipients, ...webhookSubscriptions, ...channelSubscriptions].map((r) =>
              pick(r, [`data`, `type`]),
            ),
          };
          this.resetCustomAlertModal();
          const alertApi = customAlertApi(projectId);
          const result = await (isEditing
            ? alertApi.updateCustomAlert(this.attr(`alert-id`), params)
            : alertApi.createCustomAlert(this.attr(`bookmark-id`), params));
          if (result.error) {
            this.dispatchEvent(new CustomEvent(`error`, {detail: result}));
            mixpanel.track(
              `[Custom Alerts] Failed to ${isEditing ? `update` : `create`} custom alert`,
              this.trackingProperties({error: result.error}),
            );
          } else {
            this.dispatchEvent(
              new CustomEvent(isEditing ? `update` : `create`, {
                bubbles: true,
                composed: true,
                detail: result,
              }),
            );

            const trackingProps = customAlertTrackingProperties(result);
            trackingProps[`# of External Recipient`] = validRecipients.filter((sub) => !sub.name).length;
            trackingProps[`# of Internal Recipients`] = validRecipients.filter((sub) => !!sub.name).length;
            mixpanel.track(`[Custom Alerts] ${isEditing ? `Updated` : `Created`} custom alert`, trackingProps);
          }
        },

        getSubscriptionsByType: (subscriptionType: SubscriptionType) => this.state.subscriptions[subscriptionType],

        shouldShowSlackChannelSubscriptions: () => {
          return true;
        },

        handleSlackChannelSelectChange: (ev: SelectedChannelsChangedEvent) => {
          const updatedSubscriptions: SlackChannelSubscription[] = ev.detail.selectedChannels.map((slackChannel) => {
            return {
              type: SubscriptionType.SlackChannel,
              data: {
                slack_team_id: ev.detail.teamId,
                slack_channel_id: slackChannel.id,
                slack_channel_name: slackChannel.name,
              },
            };
          });
          this.produceUpdate(({subscriptions}) => {
            subscriptions[SubscriptionType.SlackChannel] = updatedSubscriptions;
          });
        },

        updateSubscriptionData: (
          subscriptionType: SubscriptionType,
          idx: number,
          subscriptionData: Subscription['data'],
        ) => {
          this.produceUpdate((state) => {
            state.subscriptions[subscriptionType][idx].data = subscriptionData;
          });
        },

        createDefaultSubscription: (subscriptionType: SubscriptionType) => {
          let subscription;
          if (subscriptionType === SubscriptionType.SlackWebhook) {
            subscription = {
              error: null,
              data: {
                slack_webhook_url: ``,
              },
              type: subscriptionType,
            };
          } else if (subscriptionType === SubscriptionType.SlackChannel) {
            subscription = {
              error: null,
              data: {
                slack_team_id: ``,
                slack_channel_id: ``,
              },
              type: subscriptionType,
            };
          }

          this.produceUpdate((state) => {
            state.subscriptions[subscriptionType].push(subscription);
          });
        },

        deleteSubscription: (subscriptionType: SubscriptionType, idx: number) => {
          this.produceUpdate(({subscriptions}) => {
            subscriptions[subscriptionType].splice(idx, 1);
          });
        },
      },
    };
  }

  isErrorResult(results) {
    return !!results?.error;
  }

  getReportStatus(): ReportStatus {
    if (this.state.loadingChart) {
      return ReportStatus.Loading;
    } else if (this.isErrorResult(this.state.anomalyDetectionResults)) {
      return ReportStatus.Error;
    }
    return null;
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    switch (attr) {
      case `is-modal-open`:
        if (!this.isAttributeEnabled(attr)) {
          this.resetCustomAlertModal();
        }
        break;
      case `read-only`:
        this.update({readOnly: this.isAttributeEnabled(attr)});
        break;
      case `project-id`:
        // only fetch project members if the project id is not null
        if (newValue) {
          this.fetchProjectMembers();
        }
        break;
      case `alert-id`:
        this.update({isEditing: !!this.getAttribute(attr)});
        break;
      case `alert-name`:
        this.update({alertName: this.getAttribute(attr) || DEFAULT_ALERT_NAME});
        break;
      case `alert-frequency`:
        this.update({alertFrequency: this.getNumberAttribute(attr)});
        break;
      case `results-keys-and-values`:
        this.updateDefaultSelectedAlertKeys();
        break;
      case `alert-condition-op`:
        this.update({
          alertOp: this.getAttribute(attr) || CUSTOM_ALERT_OPERATOR.above,
          ...this.helpers.getAlertConditionTextAndValueFromTypeAndOp(
            this.state.alertType,
            this.getAttribute(attr) || CUSTOM_ALERT_OPERATOR.above,
          ),
        });
        break;
      case `alert-condition-threshold`:
        this.update({alertThreshold: this.getNumberAttribute(attr)});
        break;
      case `alert-condition-type`: {
        const newAlertType =
          this.getAttribute(attr) === CUSTOM_ALERT_TYPE.anomaly
            ? CUSTOM_ALERT_TYPE.absolute
            : this.getAttribute(attr) || CUSTOM_ALERT_TYPE.absolute;
        this.update({
          alertType: newAlertType,
          ...this.helpers.getAlertConditionTextAndValueFromTypeAndOp(newAlertType, this.state.alertOp),
          alertCriteriaType:
            this.getAttribute(attr) === CUSTOM_ALERT_TYPE.anomaly
              ? CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection
              : CUSTOM_ALERT_CRITERIA_TYPE.threshold,
        });
        break;
      }
      case `is-anomaly-detection-control-disabled`: {
        if (this.isAttributeEnabled(attr)) {
          this.update({
            alertCriteriaType: CUSTOM_ALERT_CRITERIA_TYPE.threshold,
          });
        }
        break;
      }
      case `alert-condition-keys`:
        this.updateSelectedAlertKeys();
        break;
      case `alert-confidence-interval`:
        this.update({confidenceInterval: this.getNumberAttribute(attr)});
        break;
      case `alert-training-mode`:
        this.update({trainingMode: this.getAttribute(attr)});
        break;
      case `recipients`:
        this.produceUpdate((state) => {
          const subscriptionsUpdate = groupBy(
            this.attr(`recipients`) || [],
            (subscription) => subscription.type,
          ) as CustomAlertState['subscriptions'];

          state.subscriptions = {
            ...RESETTABLE_STATE.subscriptions,
            ...subscriptionsUpdate,
          };
          state.recipients = state.subscriptions[SubscriptionType.Email];
        });
        break;
      case `time-unit`:
        this.update({timeUnit: this.getAttribute(attr)});
        break;
      case `date-range-interval-days`:
        this.produceUpdate((state) => {
          if (this.helpers.isLongDateRange()) {
            state.alertFrequency = CUSTOM_ALERT_FREQUENCY.Week;
          }
        });
        break;
      case `bookmark-params`:
        this.update({bookmarkParams: JSON.parse(this.getAttribute(attr))});
        this.helpers.fetchAnomalyResults();
        break;
      default:
        break;
    }
  }

  connectedCallback() {
    this.produceUpdate((state) => {
      const mpContext = getMpContext();
      state.userEmail = mpContext.userEmail;
    });

    this.update({
      ...this.helpers.getAlertConditionTextAndValueFromTypeAndOp(this.state.alertType, this.state.alertOp),
    });

    if (!this.helpers.isAnomalyDetectionToggleDisabled()) {
      this.update({alertCriteriaType: CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection});
      this.helpers.fetchAnomalyResults();
    }

    const {readOnly, recipients} = this.state;
    if (!readOnly && recipients.length === 0) {
      this.setDefaultRecipients();
    }

    mixpanel.track(`[Custom Alerts] Opened custom alert modal`, this.trackingProperties());
    super.connectedCallback();
  }

  hasAnySegmentGroup() {
    return this.attr(`report-type`) === BookmarkType.Insights;
  }

  async fetchProjectMembers() {
    const projectId = this.attr(`project-id`);
    const projectMembers = (await projectApi(projectId).getGrantedUsersForProject()).members
      .map((member) => pick(member, [`email`, `name`]))
      .sort(
        baseComparator<{name: string}, string>({transform: (item) => item.name}),
      );
    this.update({allProjectMembers: uniqWith(projectMembers, isEqual), projectMembersLoaded: true});
  }

  // For custom alerts improvements, we are no longer displaying the step selection for funnel alerts since it is a
  // value customers couldn't edit. We've decided to remove it to simplify the UI.
  getResultKeysAndValues(): TODO | string[] {
    if (this.hasExcludedLastStepKey()) {
      return this.attr(`results-keys-and-values`)?.filter((key) => key.displayName !== `Step` && key.name !== `$step`);
    }
    return this.attr(`results-keys-and-values`);
  }

  getDefaultSelectedAlertKeys() {
    // pick the first value by default
    return (this.attr(`results-keys-and-values`) || []).map((key) => ({
      header: key.name,
      displayHeader: key.displayName,
      value: key.values && key.values.length > 0 ? key.values[0] : null,
      displayValue: key.displayValues && key.displayValues.length > 0 ? key.displayValues[0] : null,
    }));
  }

  updateDefaultSelectedAlertKeys() {
    this.update({selectedAlertKeys: this.getDefaultSelectedAlertKeys()});
  }

  updateSelectedAlertKeys() {
    let selectedAlertKeys = this.attr(`alert-condition-keys`) || [];
    if (selectedAlertKeys.length > 0 && isString(selectedAlertKeys[0])) {
      // old format with only list of values
      selectedAlertKeys = selectedAlertKeys.map((key) => ({
        header: null,
        value: key,
      }));
    }
    this.update({selectedAlertKeys});
  }

  hasExcludedLastStepKey() {
    return this.attr(`report-type`) === BookmarkType.Funnels;
  }

  // For custom alerts improvements, we are no longer displaying the step selection for funnel alerts since it is a
  // value customers couldn't edit. We've decided to remove it to simplify the UI.
  getSelectedAlertKeysToDisplay() {
    if (this.hasExcludedLastStepKey()) {
      return this.state.selectedAlertKeys?.filter((key) => key.displayHeader !== `Step` && key.header !== `$step`);
    }
    return this.state.selectedAlertKeys;
  }

  hasSelectedAlertKeyForAnySegment(): boolean {
    return this.getSelectedAlertKeysToDisplay().some((alertKey) => alertKey.value === ANY_VALUE);
  }

  trackingProperties(extraProps = {}) {
    return {
      'Project ID': this.attr(`project-id`),
      'Bookmark ID': this.attr(`bookmark-id`),
      'Custom Alert ID': this.attr(`alert-id`),
      ...extraProps,
    };
  }

  validateName(nameStr) {
    let {errors} = this.state;
    let error = null;
    if (nameStr.trim().length === 0) {
      error = CUSTOM_ALERT_VALIDATION_ERROR.INVALID_NAME;
    }
    errors = {...errors, alertName: error};
    this.update({errors});
    return error;
  }

  validateThreshold({thresholdStr = null} = {}) {
    let {alertThreshold, alertType, errors} = this.state;
    let error = null;
    if (
      isString(thresholdStr) &&
      thresholdStr.length > 0 &&
      (!isFinite(parseFloat(thresholdStr)) || isNaN(parseFloat(thresholdStr)))
    ) {
      error = CUSTOM_ALERT_VALIDATION_ERROR.INVALID_THRESHOLD;
    } else {
      const floatThreshold = thresholdStr ? parseFloat(thresholdStr) : alertThreshold;
      if (this.attr(`is-percentage-threshold`) && (floatThreshold > 100 || floatThreshold < 0)) {
        error = CUSTOM_ALERT_VALIDATION_ERROR.INVALID_RATIO_THRESHOLD;
      } else if (alertType === CUSTOM_ALERT_TYPE.relativePercent && floatThreshold < 0) {
        error = CUSTOM_ALERT_VALIDATION_ERROR.INVALID_RELATIVE_PERCENTAGE;
      }
    }
    errors = {...errors, alertThreshold: error};
    this.update({errors});
    return error;
  }

  validateMultiKeyCondition() {
    const {errors} = this.state;
    const alertConditionKeys = this.hasEmptyConditionKeys() ? CUSTOM_ALERT_VALIDATION_ERROR.MISSING_VALUE : null;
    this.update({
      errors: {...errors, alertConditionKeys},
    });
  }

  hasEmptyConditionKeys() {
    const {isEditing, selectedAlertKeys} = this.state;

    const isCreatingWithMultiKey = this.getResultKeysAndValues() && !isEditing;
    return isCreatingWithMultiKey && !selectedAlertKeys.every((key) => !isNil(key.value));
  }

  isNonEmptyValidRecipient(recipient) {
    return (
      !recipient.invalidAddress && recipient.data && recipient.data.recipient && recipient.data.recipient.length > 0
    );
  }

  setDefaultRecipients() {
    this.produceUpdate((state) => {
      state.recipients = [];
      if (state.userEmail) {
        state.recipients.push({
          error: null,
          data: {
            recipient: state.userEmail,
          },
          type: SubscriptionType.Email,
        });
      }
    });
  }

  // TODO: change this to instantiate rather than reset on close
  resetCustomAlertModal() {
    this.produceUpdate((state) => {
      Object.assign(state, RESETTABLE_STATE);
    });
    this.setDefaultRecipients();
    if (!this.attr(`is-editing-mode`)) {
      this.updateDefaultSelectedAlertKeys();
    }
  }

  public produceUpdate(producer: (state: CustomAlertState) => void): void {
    this.update(produce(this.state, producer));
  }

  setElmRef(ref: keyof CustomAlertState['elmRefs'], elm: HTMLInputElement | AutoResizingInput): void {
    this.produceUpdate((state) => {
      state.elmRefs[ref] = elm;
    });
  }
}

export const CustomAlertEl = defineAndMakeJsxFactory(`mp-custom-alert-modal`, CustomAlert);
export const CustomAlertReact = panel2react(`mp-custom-alert-modal`, CustomAlert);
